import React from 'react';
import { FileText, Calendar, ClipboardList, ArrowRight } from 'lucide-react';
import { OutlineButton } from './common/Button';
// Flag to control visibility of the recent articles section
const SHOW_RECENT_ARTICLES = false;
export function EducationalContent() {
  return <section id="resources" className="py-16 bg-white" data-id="element-114">
      <div className="container mx-auto px-4" data-id="element-115">
        <div className="text-center mb-12" data-id="element-116">
          <div className="flex justify-center mb-4" data-id="element-117">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/ft96BQXpN2jabTTRnFyFuA/crowninspectionlogosmall.png" alt="Crown icon" className="w-10 h-10" data-id="element-118" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1B365D] mb-4" data-id="element-119">
            Home Inspection Knowledge Center
          </h2>
          <p className="text-[#2C3E50] text-lg max-w-3xl mx-auto" data-id="element-120">
            Resources and guides to help you understand the inspection process
            and make informed decisions.
          </p>
        </div>
        <div className="flex justify-center" data-id="element-121">
          <div className="max-w-md w-full" data-id="element-122">
            <ResourceCard icon={<Calendar size={32} className="text-[#0077BE]" data-id="element-124" />} title="Seasonal Home Maintenance Calendar" description="Keep your Florida home in top condition with this seasonal maintenance schedule." downloadable={true} data-id="element-123" />
          </div>
        </div>
        {/* Recent Articles section - hidden but preserved for future use */}
        {SHOW_RECENT_ARTICLES && <div className="mt-12" data-id="element-125">
            <div className="flex items-center mb-6" data-id="element-126">
              <h3 className="text-2xl font-bold text-[#1B365D] mr-3" data-id="element-127">
                Recent Articles
              </h3>
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/ft96BQXpN2jabTTRnFyFuA/crowninspectionlogosmall.png" alt="Crown icon" className="w-6 h-6" data-id="element-128" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-129">
              <BlogPostCard title="5 Common Issues Found in Florida Homes" excerpt="Learn about the most common problems our inspectors find in Southwest Florida homes and how to address them." date="August 15, 2023" image="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" data-id="element-130" />
              <BlogPostCard title="Hurricane Season Preparation: Is Your Home Ready?" excerpt="Critical steps to prepare your home for hurricane season and how a professional inspection can identify vulnerabilities." date="July 28, 2023" image="https://images.unsplash.com/photo-1527482797697-8795b05a13fe?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" data-id="element-131" />
              <BlogPostCard title="Understanding Wind Mitigation: Save on Insurance" excerpt="How wind mitigation inspections work and why they can save Florida homeowners hundreds on insurance premiums." date="July 10, 2023" image="https://images.unsplash.com/photo-1618090584176-7132b9911657?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1074&q=80" data-id="element-132" />
            </div>
            <div className="mt-8 text-center" data-id="element-133">
              <a href="#" className="text-[#0077BE] font-bold hover:underline inline-flex items-center" data-id="element-134">
                View All Articles
                <ArrowRight size={16} className="ml-2" data-id="element-135" />
              </a>
            </div>
          </div>}
      </div>
    </section>;
}
interface ResourceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  downloadable?: boolean;
}
function ResourceCard({
  icon,
  title,
  description,
  downloadable
}: ResourceCardProps) {
  return <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-md flex flex-col h-full transform hover:shadow-lg transition-shadow duration-300 relative" data-id="element-136">
      <div className="mb-4" data-id="element-137">{icon}</div>
      <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-138">{title}</h3>
      <p className="text-[#2C3E50] text-sm mb-4 flex-grow" data-id="element-139">{description}</p>
      {downloadable && <OutlineButton size="sm" fullWidth data-id="element-140">
          Download PDF
        </OutlineButton>}
      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/ft96BQXpN2jabTTRnFyFuA/crowninspectionlogosmall.png" alt="Crown icon" className="w-5 h-5 absolute top-3 right-3 opacity-30" data-id="element-141" />
    </div>;
}
interface BlogPostCardProps {
  title: string;
  excerpt: string;
  date: string;
  image: string;
}
function BlogPostCard({
  title,
  excerpt,
  date,
  image
}: BlogPostCardProps) {
  return <div className="bg-white rounded-lg overflow-hidden shadow-md transform hover:shadow-lg transition-shadow duration-300" data-id="element-142">
      <img src={image} alt={title} className="w-full h-48 object-cover" data-id="element-143" />
      <div className="p-6" data-id="element-144">
        <div className="text-sm text-gray-500 mb-2" data-id="element-145">{date}</div>
        <h3 className="text-xl font-bold text-[#1B365D] mb-2" data-id="element-146">{title}</h3>
        <p className="text-[#2C3E50] text-sm mb-4" data-id="element-147">{excerpt}</p>
        <a href="#" className="text-[#0077BE] font-medium hover:underline inline-flex items-center" data-id="element-148">
          Read More
          <ArrowRight size={14} className="ml-1" data-id="element-149" />
        </a>
      </div>
    </div>;
}